import {
  UNCATEGORIZED,
  type ProductCategoryGroup,
  type ProductCategoryGroups,
} from '@avrahami/contracts';

/**
 * Product lists are shown under their categories: category, then its sub-categories, then the
 * products. These helpers turn the flat categories an API answers with into that tree.
 */

export interface CategoryNode {
  id: string;
  name: string;
  /** Products placed directly in the category. */
  direct: number;
  /** Products in the category and in every category below it. */
  total: number;
  children: CategoryNode[];
}

/**
 * The categories that have products (their own or below them) as a tree, in the order given. A
 * category whose parent is missing, or that sits in a parent cycle, is shown at the top level, so
 * its products are never lost.
 */
export function categoryNodes(groups: readonly ProductCategoryGroup[]): CategoryNode[] {
  const ids = new Set(groups.map((group) => group.id));
  const children = new Map<string | null, ProductCategoryGroup[]>();
  for (const group of groups) {
    const parent = group.parentId !== null && ids.has(group.parentId) ? group.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), group]);
  }
  const seen = new Set<string>();
  const build = (parent: string | null): CategoryNode[] => {
    const nodes: CategoryNode[] = [];
    for (const group of children.get(parent) ?? []) {
      if (seen.has(group.id)) continue;
      seen.add(group.id);
      const below = build(group.id);
      const total = below.reduce((sum, node) => sum + node.total, group.productCount);
      if (total === 0) continue;
      nodes.push({
        id: group.id,
        name: group.name,
        direct: group.productCount,
        total,
        children: below,
      });
    }
    return nodes;
  };
  const top = build(null);
  // What a parent cycle kept out of the walk.
  for (const group of groups) {
    if (seen.has(group.id) || group.productCount === 0) continue;
    seen.add(group.id);
    top.push({
      id: group.id,
      name: group.name,
      direct: group.productCount,
      total: group.productCount,
      children: [],
    });
  }
  return top;
}

/** The category and every category below it. */
export function subtreeIds(node: CategoryNode): string[] {
  return [node.id, ...node.children.flatMap(subtreeIds)];
}

/** Whether any product is listed under the categories (or in none). */
export function hasGroupedProducts(groups: ProductCategoryGroups): boolean {
  return groups.uncategorized > 0 || groups.items.some((group) => group.productCount > 0);
}

/**
 * Counts a list that is already in hand under the categories: an item counts in every known
 * category it names, and as uncategorized when it names none of them.
 */
export function groupItems(
  categories: readonly Omit<ProductCategoryGroup, 'productCount'>[],
  items: readonly { categoryIds: readonly string[] }[],
): ProductCategoryGroups {
  const counts = new Map(categories.map((category) => [category.id, 0]));
  let uncategorized = 0;
  for (const item of items) {
    const known = item.categoryIds.filter((id) => counts.has(id));
    if (known.length === 0) uncategorized += 1;
    for (const id of new Set(known)) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return {
    items: categories.map((category) => ({
      id: category.id,
      parentId: category.parentId,
      name: category.name,
      productCount: counts.get(category.id) ?? 0,
    })),
    uncategorized,
  };
}

/** Whether an item in hand is listed under `categoryId` (`knownIds`: the categories shown). */
export function itemInCategory(
  item: { categoryIds: readonly string[] },
  categoryId: string,
  knownIds: ReadonlySet<string>,
): boolean {
  if (categoryId === UNCATEGORIZED) return !item.categoryIds.some((id) => knownIds.has(id));
  return item.categoryIds.includes(categoryId);
}
